import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { UsersRound, GraduationCap, Sparkles, Rocket, LogIn, UserPlus } from 'lucide-react';
import {
  accountsEnabled, ADULT_AUTH_UNAVAILABLE, passwordMinLengthForMode,
  request, requestWithAdultRefresh, requiresEmailConfirmation, restoreAdultSession,
} from './AdultAuthPanel';
import './AdultAccountPage.css';
import { SpaceIllustration } from './AccountsVisuals';

const PURPOSES = [
  { purpose: 'FAMILY', Icon: UsersRound, title: 'Familia', description: 'Acompaña el aprendizaje de tus pequeños exploradores.' },
  { purpose: 'TEACHER', Icon: GraduationCap, title: 'Maestro', description: 'Crea tus clases y acompaña a tus estudiantes.' },
  { purpose: 'BOTH', Icon: Sparkles, title: 'Ambos', description: 'Con tu familia y también como maestro.' },
];

export default function AdultAccountPage() {
  const { search } = useLocation();
  const requestedEntry = new URLSearchParams(search).get('entry');
  // Context only; destination still requires the refreshed server capability.
  const entry = ['family', 'teacher'].includes(requestedEntry) ? requestedEntry : null;
  const entryPurpose = entry === 'family' ? 'FAMILY' : 'TEACHER';
  const [profile, setProfile] = useState(null);
  const [state, setState] = useState('loading');
  const [mode, setMode] = useState('welcome');
  const [registrationPurpose, setRegistrationPurpose] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const guardian = profile?.roles?.includes('GUARDIAN');
  const teacher = profile?.roles?.includes('TEACHER');
  const hasCapacity = guardian || teacher;

  function applySession(adult) {
    if (adult === ADULT_AUTH_UNAVAILABLE) {
      setState('unavailable');
      return;
    }
    setProfile(adult);
    setState('ready');
  }

  useEffect(() => {
    if (!accountsEnabled) return undefined;
    // Confirmation is completed by Supabase. We require login here and discard
    // any returned fragment rather than persisting or treating it as a session.
    if (window.location.hash) {
      window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
      setMode('login');
    }
    let active = true;
    restoreAdultSession().then((adult) => { if (active) applySession(adult); });
    return () => { active = false; };
  }, []);

  if (!accountsEnabled) return <Navigate to="/" replace />;

  function changeMode(next) { setMode(next); setNotice(''); if (next !== 'register') setRegistrationPurpose(null); }

  function chooseRegistrationPurpose(purpose) { setRegistrationPurpose(purpose); changeMode('register'); }

  async function retry() {
    setBusy(true);
    try { applySession(await restoreAdultSession()); }
    finally { setBusy(false); }
  }

  async function submit(event) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const email = String(form.get('email') || '').trim();
    const password = String(form.get('password') || '');
    if (mode === 'register' && password !== String(form.get('confirmation') || '')) {
      setNotice('Las contraseñas no coinciden.');
      return;
    }
    setBusy(true);
    setNotice('');
    try {
      if (mode === 'recovery') {
        await request('/auth/password-recovery', {
          method: 'POST', body: JSON.stringify({ email, redirect_to: `${window.location.origin}/adult-password-reset` }),
        });
        setNotice('Si existe una cuenta para ese correo, recibirás instrucciones para recuperar tu contraseña.');
        return;
      }
      const adult = await request(mode === 'register' ? '/auth/register' : '/auth/login', {
        method: 'POST', body: JSON.stringify(mode === 'register'
          ? { display_name: String(form.get('display_name') || '').trim(), email, password }
          : { email, password }),
      });
      if (requiresEmailConfirmation(mode, adult)) {
        formElement.reset();
        setProfile(null);
        setRegistrationPurpose(null);
        setMode('confirmation');
        return;
      }
      if (!adult.session_available) {
        setNotice('No fue posible iniciar sesión. Revisa tus datos e inténtalo de nuevo.');
        return;
      }
      formElement.reset();
      // Always render capabilities obtained from the server, never form choices.
      const updated = await requestWithAdultRefresh('/auth/me');
      setProfile(updated);
      setMode('welcome');
    } catch (error) {
      setNotice(error?.status === 503
        ? 'Tu cuenta no está disponible temporalmente. Inténtalo de nuevo; puedes seguir jugando gratis.'
        : mode === 'register'
          ? 'No pudimos completar la solicitud de registro. Revisa los datos y los requisitos de contraseña.'
          : mode === 'recovery'
            ? 'No fue posible enviar las instrucciones. Inténtalo de nuevo.'
            : 'No pudimos iniciar sesión. Revisa tus datos o confirma tu correo.');
    } finally { setBusy(false); }
  }

  async function onboard(purpose) {
    setBusy(true);
    setNotice('');
    try {
      await requestWithAdultRefresh('/auth/onboarding', { method: 'POST', body: JSON.stringify({ purpose }) });
      const updated = await requestWithAdultRefresh('/auth/me');
      setProfile(updated);
      setMode('welcome');
    } catch (error) {
      setNotice(error?.status === 403
        ? 'Esta capacidad no está disponible para tu cuenta. Una capacidad revocada requiere revisión; no se puede reactivar aquí.'
        : 'No fue posible actualizar tu cuenta. Inténtalo de nuevo; puedes seguir jugando gratis.');
    } finally { setBusy(false); }
  }

  async function signOut() {
    setBusy(true);
    setNotice('');
    try {
      // Logout must clear cookies even with an expired access token; no refresh.
      await request('/auth/logout', { method: 'POST' });
      setProfile(null);
      setMode('welcome');
      setNotice('Sesión cerrada. ¡Hasta pronto!');
    } catch (_error) { setNotice('No fue posible cerrar sesión. Inténtalo de nuevo.'); }
    finally { setBusy(false); }
  }

  if (state === 'ready' && profile && mode === 'welcome' && entry &&
      (entry === 'family' ? guardian : teacher)) return <Navigate to={`/${entry}`} replace />;

  return <main className="tmag-account">
    <nav className="tmag-account__nav" aria-label="Navegación de cuenta">
      <Link to="/">← VOLVER A JUGAR GRATIS</Link><span>TechnoMathPR</span>
    </nav>
    <section className="tmag-account__panel" aria-labelledby="account-title">
      <header className="tmag-account__hero">
        <div><p className="tmag-account__eyebrow">TU ESPACIO EN LA GALAXIA</p>
        <h1 id="account-title">{entry ? entry === 'family' ? 'Tu familia en TechnoMathPR' : 'Tu clase en TechnoMathPR' : 'Entrar a TechnoMathPR'}</h1>
        <p className="tmag-account__intro">Grandes aventuras. Pequeños exploradores.</p></div>
        <SpaceIllustration />
      </header>
      {state === 'loading' && <p role="status">Verificando tu sesión…</p>}
      {state === 'unavailable' && <>
        <p role="status">La cuenta está temporalmente no disponible. Tu sesión no se ha cerrado.</p>
        <div className="tmag-account__actions"><button type="button" onClick={retry} disabled={busy}>REINTENTAR</button><Link className="tmag-account__guest" to="/">VOLVER A JUGAR GRATIS</Link></div>
      </>}
      {state === 'ready' && !profile && <>
        {mode === 'welcome' && entry && <>
          <h2>{entry === 'family' ? 'Acompaña a tus pequeños exploradores' : 'Prepara la aventura de tu clase'}</h2>
          <div className="tmag-account__actions">
            <button type="button" onClick={() => changeMode('login')}>INICIAR SESIÓN</button>
            <button type="button" className="tmag-account__secondary" onClick={() => chooseRegistrationPurpose(entryPurpose)}>CREAR CUENTA</button>
          </div>
          <Link className="tmag-account__guest" to="/account">Ver todas las opciones de cuenta</Link>
        </>}
        {mode === 'welcome' && !entry && <>
          <h2>¿Cómo quieres entrar?</h2>
          <div className="tmag-account__paths tmag-account__paths--entry" aria-label="Elige tu camino">
            <article className="tmag-account__path tmag-account__path--student"><Rocket aria-hidden="true" /><strong>SOY ESTUDIANTE</strong><span>Tengo mi código de acceso</span><Link className="tmag-account__path-action" to="/student">ENTRAR COMO ESTUDIANTE</Link></article>
            <article className="tmag-account__path tmag-account__path--existing"><LogIn aria-hidden="true" /><strong>YA TENGO UNA CUENTA</strong><span>Para familias y maestros</span><button type="button" onClick={() => changeMode('login')}>INICIAR SESIÓN</button></article>
            <article className="tmag-account__path tmag-account__path--new"><UserPlus aria-hidden="true" /><strong>SOY NUEVO EN TECHNOMATHPR</strong><span>Crear una cuenta para familia o maestro</span><button type="button" onClick={() => changeMode('register-purpose')}>CREAR CUENTA</button></article>
          </div>
          <Link className="tmag-account__guest" to="/">JUGAR GRATIS →</Link>
          <p className="tmag-account__hint">Familias y maestros usan una cuenta adulta. Los estudiantes tienen su propio acceso.</p>
        </>}
        {mode === 'register-purpose' && <>
          <button type="button" className="tmag-account__text" onClick={() => changeMode('welcome')}>← Elegir otro camino</button>
          <h2>¿Cómo usarás TechnoMathPR?</h2>
          <p className="tmag-account__hint">Elige cómo quieres empezar. Una misma cuenta adulta puede tener los usos Familia y Maestro; no necesitas registrarte de nuevo.</p>
          <div className="tmag-account__cards">
            {PURPOSES.filter((p) => p.purpose !== 'BOTH').map((p) => <button type="button" className="tmag-account__card" key={p.purpose} onClick={() => chooseRegistrationPurpose(p.purpose)}><p.Icon aria-hidden="true" className="tmag-account__symbol" /><strong>{p.title}</strong><span>{p.description}</span></button>)}
          </div>
          <button type="button" className="tmag-account__text" onClick={() => chooseRegistrationPurpose('BOTH')}>También soy familia y maestro</button>
          <button type="button" className="tmag-account__text" onClick={() => changeMode('login')}>Ya tengo una cuenta</button>
        </>}
        {mode === 'confirmation' && <>
          <h2>Solicitud de registro recibida</h2>
          <p>Si corresponde confirmar este correo, recibirás un enlace para continuar. Esta respuesta no confirma la creación de una cuenta nueva.</p>
          <p>Si ya tienes una cuenta, inicia sesión con tu contraseña habitual. Desde tu cuenta puedes solicitar añadir el uso Familia o Maestro, sin volver a registrarte.</p>
          <button type="button" onClick={() => changeMode('login')}>IR A INICIAR SESIÓN</button>
        </>}
        {['login', 'register', 'recovery'].includes(mode) && <>
          <button type="button" className="tmag-account__text" disabled={busy} onClick={() => changeMode(mode === 'register' ? 'register-purpose' : 'welcome')}>{mode === 'register' ? '← Cambiar mi elección' : '← Elegir otro camino'}</button>
          <h2>{mode === 'register' ? 'Crea tu cuenta adulta' : mode === 'recovery' ? 'Recupera tu contraseña' : '¡Bienvenido de nuevo!'}</h2>
          <p className="tmag-account__hint">{mode === 'recovery' ? 'Te enviaremos un enlace para volver a tu cuenta.' : mode === 'register' ? registrationPurpose === 'FAMILY' ? 'Una cuenta para acompañar a tu familia.' : registrationPurpose === 'TEACHER' ? 'Una cuenta para acompañar a tus estudiantes.' : 'Una cuenta para tu familia y tus clases.' : 'Entra con el correo y la contraseña de tu cuenta adulta.'}</p>
          {mode === 'register' && <p className="tmag-account__hint">¿Ya usas Familia o Maestro? Inicia sesión con la misma cuenta para solicitar añadir el otro uso. Elegir aquí no cambia los permisos de una cuenta existente.</p>}
          <form key={mode} onSubmit={submit}>
            {mode === 'register' && <label>Nombre<input name="display_name" autoComplete="name" minLength="1" maxLength="120" required disabled={busy} /></label>}
            <label>Correo<input name="email" type="email" autoComplete="email" required disabled={busy} /></label>
            {mode !== 'recovery' && <label>Contraseña<input name="password" type="password" autoComplete={mode === 'register' ? 'new-password' : 'current-password'} minLength={passwordMinLengthForMode(mode)} maxLength="128" required disabled={busy} /></label>}
            {mode === 'register' && <>
              <label>Confirmar contraseña<input name="confirmation" type="password" autoComplete="new-password" minLength="8" maxLength="128" required disabled={busy} /></label>
              <p className="tmag-account__hint">Usa al menos 8 caracteres y elige una contraseña segura.</p>
            </>}
            <button type="submit" disabled={busy}>{busy ? 'UN MOMENTO…' : mode === 'register' ? 'CREAR CUENTA' : mode === 'recovery' ? 'ENVIAR INSTRUCCIONES' : 'INICIAR SESIÓN'}</button>
          </form>
          <div className="tmag-account__actions">
            <button type="button" className="tmag-account__text" disabled={busy} onClick={() => changeMode(mode === 'login' ? 'register-purpose' : 'login')}>{mode === 'login' ? 'Crear cuenta' : 'Ya tengo una cuenta'}</button>
            {mode === 'login' && <button type="button" className="tmag-account__text" disabled={busy} onClick={() => changeMode('recovery')}>Recuperar contraseña</button>}
          </div>
        </>}
      </>}
      {state === 'ready' && profile && <>
        <p className="tmag-account__identity">{profile.email}</p>
        {(!hasCapacity || mode === 'purpose') ? <>
          <h2>{hasCapacity ? 'Añade otra forma de usar TechnoMathPR' : '¿Cómo usarás TechnoMathPR?'}</h2>
          <p className="tmag-account__hint">Usarás la misma cuenta y el mismo correo. Esta solicitud conserva tus accesos actuales; un uso revocado requiere revisión.</p>
          <div className="tmag-account__cards">
            {PURPOSES.filter((p) => !hasCapacity || (p.purpose === 'FAMILY' && !guardian) || (p.purpose === 'TEACHER' && !teacher)).map((p) => (
              <button type="button" className="tmag-account__card" key={p.purpose} disabled={busy} onClick={() => onboard(p.purpose)}>
                <p.Icon aria-hidden="true" className="tmag-account__symbol" /><strong>{p.title}</strong><span>{p.description}</span>
              </button>
            ))}
          </div>
          {hasCapacity && <button type="button" className="tmag-account__text" disabled={busy} onClick={() => changeMode('welcome')}>VOLVER A MI CUENTA</button>}
        </> : <>
          <h2>Tu tripulación te espera</h2>
          <div className="tmag-account__cards">
            {guardian && <Link className="tmag-account__card" to="/family"><UsersRound aria-hidden="true" className="tmag-account__symbol" /><strong>Mi familia</strong><span>Tus pequeños exploradores y sus próximas aventuras.</span><b>IR A FAMILIA →</b></Link>}
            {teacher && <Link className="tmag-account__card" to="/teacher"><GraduationCap aria-hidden="true" className="tmag-account__symbol" /><strong>Mis clases</strong><span>Agrega estudiantes y prepara sus accesos para jugar.</span><b>IR A MAESTRO →</b></Link>}
          </div>
          {!(guardian && teacher) && <>
            <p className="tmag-account__hint">Para usar también {guardian ? 'Maestro' : 'Familia'}, solicita añadir ese uso a esta misma cuenta. No necesitas registrarte otra vez.</p>
            <button type="button" className="tmag-account__text" disabled={busy} onClick={() => changeMode('purpose')}>AÑADIR USO COMO {guardian ? 'MAESTRO' : 'FAMILIA'}</button>
          </>}
        </>}
        <button type="button" className="tmag-account__secondary" disabled={busy} onClick={signOut}>CERRAR SESIÓN</button>
      </>}
      {notice && <p className="tmag-account__notice" role="status">{notice}</p>}
    </section>
  </main>;
}
